import * as React from 'react'

import NextApp, { AppInitialProps, AppProps } from 'next/app'
import getConfig from 'next/config'
import Head from 'next/head'

import { setServiceObject, withSentry } from '@grandvisionhq/analytics'
import { AuthProvider } from '@grandvisionhq/auth'
import { BloomreachApi, RuntimeContext } from '@grandvisionhq/bloomreach'
import { GoogleMapsProvider, MediaQuery, ToastContainer } from '@grandvisionhq/common'
import { StockIndicatorProvider } from '@grandvisionhq/inventory'
import { AddressConfigurationProvider } from '@grandvisionhq/locations'
import { ExtendedNextAppContext, withApollo, withRedux } from '@grandvisionhq/next'
import { AdyenProvider } from '@grandvisionhq/payments'
import {
  ApiConfigProvider,
  FeatureToggleProvider,
  IntlProvider,
  ReduxProvider,
  StoreWithPersistor,
} from '@grandvisionhq/state'
import { compose } from '@grandvisionhq/utils'

import { withConsent } from '../components/hocs/with-consent'
import { default as CookiePro } from '~/components/third-party/CookiePro'
import {
  getAddressConfig,
  getAdyenConfig,
  getApiConfig,
  getAuthConfig,
  getGoogleMapsConfig,
} from '~/configuration'
import { INTL_DEFAULTS, intlConfigMap } from '~/configuration/intl'
import { isMyAccountEnabled } from '~/util/feature-toggle'

import '../styles/index.scss'

const { publicRuntimeConfig } = getConfig()
const bloomreachSettings = publicRuntimeConfig.bloomreach ?? {}
const features = publicRuntimeConfig.grandvision.features ?? []
const featureConfigs = publicRuntimeConfig.grandvision.featureConfigs ?? {}

const addressSettings = getAddressConfig()
const adyenSettings = getAdyenConfig()
const authSettings = getAuthConfig()
const apiConfig = getApiConfig()
const googleMapsSettings = getGoogleMapsConfig()

type InitialProps = {
  locale: string
  labels: Record<string, string>
  initialBreakpoint: MediaQuery.BreakpointName
}

type Props = AppProps & InitialProps & { store: StoreWithPersistor }

export const App = ({ Component, pageProps, store, labels, locale, initialBreakpoint }: Props) => {
  // Omit some keys from publicRuntimeConfig before passing it to runtime context
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    adyen,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    bloomreach,
    deliveryIndicator,
    stockIndicator,
    ...cleanPublicRuntimeConfig
  } = publicRuntimeConfig

  React.useEffect(setServiceObject, [])

  const intlSettings = intlConfigMap[locale]

  return (
    <React.StrictMode>
      <Head>
        {/*https://github.com/vercel/next.js/blob/master/errors/no-document-viewport-meta.md*/}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>
      <ReduxProvider store={store}>
        <FeatureToggleProvider enabledFeatures={features} enabledFeatureConfigs={featureConfigs}>
          <IntlProvider settings={intlSettings} labels={labels}>
            <ApiConfigProvider apiConfig={apiConfig}>
              <GoogleMapsProvider settings={googleMapsSettings}>
                <AdyenProvider settings={adyenSettings}>
                  <StockIndicatorProvider settings={stockIndicator}>
                    <MediaQuery.Provider initialBreakpoint={initialBreakpoint}>
                      <AuthProvider enabled={isMyAccountEnabled()} settings={authSettings}>
                        <AddressConfigurationProvider settings={addressSettings}>
                          <RuntimeContext.Provider
                            value={{ ...cleanPublicRuntimeConfig, headComponent: Head }}
                          >
                            <ToastContainer />
                            <CookiePro />
                            <Component {...pageProps} labels={labels} store={store} />
                          </RuntimeContext.Provider>
                        </AddressConfigurationProvider>
                      </AuthProvider>
                    </MediaQuery.Provider>
                  </StockIndicatorProvider>
                </AdyenProvider>
              </GoogleMapsProvider>
            </ApiConfigProvider>
          </IntlProvider>
        </FeatureToggleProvider>
      </ReduxProvider>
    </React.StrictMode>
  )
}

App.getInitialProps = async (appContext: ExtendedNextAppContext): Promise<InitialProps> => {
  const { ctx } = appContext
  const locale = ctx.req?.locale?.toString() || INTL_DEFAULTS.locale
  const initialBreakpoint =
    (ctx.req?.mediaQueryBreakpoint as MediaQuery.BreakpointName) || MediaQuery.BreakpointName.PHONE

  const tasks: [Promise<AppInitialProps>, Promise<Record<string, string> | undefined> | null] = [
    NextApp.getInitialProps(appContext),
    null,
  ]

  const rehydratedLabels = ctx.res?.locals.labels
  if (!rehydratedLabels) {
    tasks[1] = BloomreachApi.getLabels(
      bloomreachSettings.apiUrl,
      bloomreachSettings.channel,
      ['*'],
      locale
    )
  }

  const [appProps, labels] = await Promise.all(tasks)

  return {
    ...appProps,
    locale,
    initialBreakpoint,
    labels: rehydratedLabels || labels || {},
  }
}

export default compose(withSentry, withRedux, withApollo(), withConsent())(App)
